import { DropzoneFile } from 'components/Dropzone/utils';
import { ObjectValues } from 'types';
import { DataRoom, DataRoomSecuritySettings } from './DataRoom';
import { DataRoomMember } from './DataRoomMember';
import { User } from 'models/User';
import { DataRoomNode } from './DataRoomNode';
import { CurrentLocation } from 'components/DataRoomNodeCurrentLocation/DataRoomNodeCurrentLocation.component';
import { NotificationMedium } from 'models/Notification';

export const DATA_ROOM_RFI_FILE_TYPES: Record<string, string> = {
  IM: 'im',
  PL: 'pl',
  ASSET_REGISTER: 'asset_register',
  BALANCE_SHEET: 'balance_sheet',
  CUSTOMER_LIST: 'customer_list',
  SUPPLIER_LIST: 'supplier_list',
  BAS_STATEMENT: 'bas_statement',
  SHAREHOLDER_REGISTER: 'shareholder_register',
} as const;

export const ADDITIONAL_DOCUMENTS = 'additional_documents';

export const DATA_ROOM_RFI_FILE_TYPES_WITHOUT_PERIOD = [
  DATA_ROOM_RFI_FILE_TYPES.IM,
  DATA_ROOM_RFI_FILE_TYPES.ASSET_REGISTER,
  DATA_ROOM_RFI_FILE_TYPES.CUSTOMER_LIST,
  DATA_ROOM_RFI_FILE_TYPES.SUPPLIER_LIST,
  DATA_ROOM_RFI_FILE_TYPES.SHAREHOLDER_REGISTER,
];

export const DATA_ROOM_RFI_FILENAME = {
  [DATA_ROOM_RFI_FILE_TYPES.IM]: 'rfiIM',
  [DATA_ROOM_RFI_FILE_TYPES.PL]: 'rfiPL',
  [DATA_ROOM_RFI_FILE_TYPES.ASSET_REGISTER]: 'rfiAssetRegister',
  [DATA_ROOM_RFI_FILE_TYPES.BALANCE_SHEET]: 'rfiBalanceSheet',
  [DATA_ROOM_RFI_FILE_TYPES.CUSTOMER_LIST]: 'rfiCustomerList',
  [DATA_ROOM_RFI_FILE_TYPES.SUPPLIER_LIST]: 'rfiSupplierList',
  [DATA_ROOM_RFI_FILE_TYPES.BAS_STATEMENT]: 'rfiBasStatement',
  [DATA_ROOM_RFI_FILE_TYPES.SHAREHOLDER_REGISTER]: 'rfiShareholderRefister',
  other: 'rfiCustomFile',
} as const;

export const DATA_ROOM_RFI_FILE_PERIOD = {
  '2021': '2021',
  '2022': '2022',
  '2023': '2023',
  '2024': '2024',
  LAST_3_FINANCIAL_YEARS: 'last_three_financial_years',
} as const;

export const DATA_ROOM_RFI_STATUS = {
  PENDING: 'pending',
  SUBMITTED: 'submitted',
  CLOSED: 'closed',
} as const;

export const DATA_ROOM_RFI_FILE_STATUS = {
  UPLOADED: 'uploaded',
  ADDED: 'added',
  IGNORED: 'ignored',
} as const;

export type DataRoomRFIFileStatus = ObjectValues<
  typeof DATA_ROOM_RFI_FILE_STATUS
>;

export type DataRoomRFIFilePeriod = ObjectValues<
  typeof DATA_ROOM_RFI_FILE_PERIOD
>;

export type DataRoomRFIStatus = ObjectValues<typeof DATA_ROOM_RFI_STATUS>;

export type DataRoomRFIUploadFileTypes = ObjectValues<
  typeof DATA_ROOM_RFI_FILE_TYPES
>;

export type DataRoomRFI = {
  _id: string;

  /* ----------------------------- Core properties ---------------------------- */

  name: string;
  notifyOnComplete: boolean;
  notificationMedium: NotificationMedium[];
  dueDate: Date;
  twentyFourHoursBeforeDeadlineReminder: boolean;
  atDeadlineReminder: boolean;
  threeDaysAfterDeadlineReminder: boolean;
  fiveDaysAfterDeadlineReminder: boolean;
  recipients: string[];
  customNote: string;
  status: DataRoomRFIStatus;
  externalKey: string;

  automaticFileUploadToDataRoom: boolean;
  grantAccessDataRoomMemberIds: string[];

  brokerMessages: BrokerMessageType[];

  numberOfViews: number;

  allRecipientsWithReminders: DataRoomRfiRecipient[];

  lastSubmittedAt?: Date; // new file uploaded or new message arrived for broker

  // By user
  lastOpenedAt?: Date;

  // Metadata
  createdAt: Date;
  updatedAt: Date;

  /* -------------------- Virtual (non-database) properties ------------------- */
  dataRoomRFICategories: DataRoomRFICategory[];

  userId: string;
  user: Partial<User> | null;

  dataRoomMemberId: string;
  dataRoomMember: Partial<DataRoomMember> | null;

  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;
} & DataRoomSecuritySettings;

export type DataRoomRFICategory = {
  _id: string;

  name: string;
  period: DataRoomRFIFilePeriod;
  // New
  files: FileTypeRFI[];
  externalKey: string;
  type: DataRoomRFIUploadFileTypes;

  dataRoomNodeId?: string | null; // folder where files will be uploaded

  dataRoomRFIId: string;
  dataRoomRFI: Partial<DataRoomRFI> | null;

  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;
};

export type FileTypeRFI = {
  _id: string;

  file: DropzoneFile;
  status: DataRoomRFIFileStatus;
  description?: string;

  dataRoomNodeId: string;
  dataRoomNode: Partial<DataRoomNode> | null;

  dataRoomRFIFileUploaderId: string;
  dataRoomRFIFileUploader: Partial<DataRoomRFIFileUploader> | null;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
} & CurrentLocation;

export type DataRoomRFIFileUploader = {
  _id: string;

  email: string;
  firstName: string;
  lastName: string;
  companyName: string;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
};

export type BrokerMessageType = {
  content: string;
  addedAt: Date;

  dataRoomRFIFileUploaderId?: string;
  dataRoomRFIFileUploader?: Partial<DataRoomRFIFileUploader> | null;
};

export type SubmitDataRoomRFIPayload = {
  optionalNotes: {
    dataRoomRFIFileId: string;
    description: string;
  }[];
  brokerMessage: string;
};

export const CATEGORY_SORTING_VALUES = {
  FILE_NAME: 'fileName',
  UPLOADER: 'uploader',
};

export type DataRoomRfiRecipient = {
  email: string;
  forwarded: boolean;
  reminders: Date[];
};

export type DataRoomRFIReport = {
  _id: string;

  filesUploadedNumber: number;
  timesOpenedNumber: number;
  remindersSentNumber: number;

  filesUploaded: string[];
  rfiOpenedDates: Date[];
  remindersSentDates: Date[];

  dataRoomRfiId: string;
  dataRoomRfi: Partial<DataRoomRFI> | null;

  dataRoomRfiFileUploaderId: string;
  dataRoomRfiFileUploader: Partial<DataRoomRFIFileUploader> | null;
};

export type DataRoomRfiTemplate = {
  _id: string;

  name: string;
  documents: {
    name: string;
    period: DataRoomRFIFilePeriod;
  }[];
  notifyOnComplete: boolean;
  notificationMedium: NotificationMedium[];
  customNote: string;

  userId: string;
  user: Partial<User>;

  createdAt: Date;
};
